import MainContentPending from '../../pageStructure/mainContentPending';
import { useQuery } from '@tanstack/react-query';
import { useStateKey } from '../../../iwQueryClient/useStateKey';
import { getCurrentLocation } from '@iwRouter/HistoryProvider';
import { getQueryParameter } from '@iwRouter/LocationHelper';
import { PaymentFailed } from './PaymentFailed';
import { PaymentProcessing } from './PaymentProcessing';
import { PaymentSuccessful } from './PaymentSuccessful';

export const TruelayerConfirmation = () => {
    const stateKey = useStateKey();
    const location = getCurrentLocation();
    const paymentId: string | undefined = getQueryParameter(
        location,
        'paymentId'
    );
    const redirectOutcome: string | undefined = getQueryParameter(
        location,
        'redirectOutcome'
    );

    const {
        data: truelayerPaymentResponse,
        isLoading,
        isError
    } = useQuery(pollTruelayerPayment(stateKey, paymentId));

    if (isLoading) {
        return <MainContentPending />;
    }

    if (isError) {
        return <h1>Invalid Truelayer Data</h1>;
    }
    const { id, latest_status, amount, account_uuid } =
        truelayerPaymentResponse.data;

    const paymentReference = 'TL' + id;

    // it would be nice if we could have redirectOutcome == 'failure' skip the LAPI call above completely
    const failureComponent = (
        <PaymentFailed amount={amount} accountId={account_uuid} />
    );
    if (redirectOutcome == 'failure') {
        return failureComponent;
    }

    switch (latest_status) {
        case 'executed':
            return (
                <PaymentSuccessful
                    paymentReference={paymentReference}
                    amount={amount}
                />
            );
        case 'failed':
            return failureComponent;
        default:
            return <PaymentProcessing />;
    }
};

type TruelayerPaymentResponse = {
    id: number;
    latest_status: string;
    amount: number;
    account_uuid: string;
};

function pollTruelayerPayment(stateKey: string, paymentId: string | undefined) {
    return {
        queryKey: ['truelayer_payment', stateKey, paymentId],
        queryFn: async () => {
            // eslint-disable-next-line pandora/prefer-lapi-client
            const response = await fetch(
                `/api/lending/edge/truelayer_payment/${stateKey}/${paymentId}/`
            );

            if (!response.ok) {
                throw new Error('Response was not OK');
            }

            const responseJson: {
                data: TruelayerPaymentResponse;
            } = await response.json();
            return responseJson;
        },
        enabled: true,
        refetchInterval: (data: any, query: any) => {
            if (query.state.error) return false;
            if (data?.data) {
                if (['executed', 'failed'].includes(data.data.latest_status))
                    return false;
            }
            return 1500;
        }
    };
}
